import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowUpRight, Wand2, Palette, Lock, Download, UserPlus, CheckCircle2, Globe, AlertTriangle } from "lucide-react";
import { scoreTone, runRebuildPreview, rebuildPdfBlob } from "@/lib/api";
import RebuildGateDialog from "@/components/RebuildGateDialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * The credential-free "Website Makeover Preview" tool.
 * - standalone=true → shows its own URL input (used on /makeover and the homepage tab)
 * - standalone=false → shows a "Create My Free Preview" CTA for an already-known domain (analyzer results)
 * Visitors see a teaser (hero, palette, 3 recs, one before/after, score) with the full report BLURRED
 * until they provide an email or WhatsApp number — which is saved as a lead and unlocks the PDF.
 */
export default function MakeoverTool({ domain = "", standalone = false }) {
  const { user } = useAuth();
  const [url, setUrl] = useState(domain || "");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);
  const [token, setToken] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const run = async (target) => {
    const q = (target ?? url ?? "").trim();
    if (!q) { toast.error("Please enter your website address"); return; }
    setLoading(true);
    try {
      const res = await runRebuildPreview(q);
      setData(res);
      setToken(null);
      setTimeout(() => document.getElementById("makeover-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } catch {
      // errors toasted globally
    } finally {
      setLoading(false);
    }
  };

  const openPdf = async (tk = token) => {
    setDownloading(true);
    try {
      const blob = await rebuildPdfBlob(data.id, user ? {} : { token: tk });
      const win = window.open(blob, "_blank");
      setTimeout(() => URL.revokeObjectURL(blob), 60000);
      if (!win) toast.error("Please allow pop-ups to open the report.");
    } catch {
      toast.error("Could not open the report. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const unlocked = !!user || !!token;
  const primaryAction = () => (unlocked ? openPdf() : setGateOpen(true));
  const c = data?.concept;
  const dom = data?.domain || domain || url;
  const recs = ((c?.recommended_features || []).length ? c.recommended_features : (c?.before_after || []).map((b) => b.after)).slice(0, 3);
  const firstBA = (c?.before_after || [])[0];

  return (
    <div id="makeover-result" className={standalone ? "" : "mt-6"}>
      {!data ? (
        standalone ? (
          <form onSubmit={(e) => { e.preventDefault(); run(); }} className="bg-white border border-slate-200 rounded-2xl card-shadow p-2 flex flex-col sm:flex-row items-stretch gap-2" data-testid="makeover-form">
            <div className="flex items-center gap-2 flex-1 px-4">
              <Globe size={18} className="text-slate-400 shrink-0" />
              <input
                value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="Enter your website address (e.g. yourbusiness.com)"
                data-testid="makeover-url-input"
                className="w-full py-3.5 text-sm outline-none bg-transparent text-slate-900 placeholder:text-slate-400"
              />
            </div>
            <button type="submit" disabled={loading} data-testid="makeover-run-btn" className="btn-primary rounded-xl px-6 py-3.5 flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <>Create My Free Preview <ArrowUpRight size={16} /></>}
            </button>
          </form>
        ) : (
          <div className="bg-gradient-to-b from-emerald-50 to-white border border-emerald-100 rounded-2xl p-6 md:p-8 flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1"><Wand2 size={16} className="text-emerald-700" /><span className="text-xs font-bold uppercase tracking-wide text-emerald-700">Website Makeover Preview</span></div>
              <p className="font-heading text-xl md:text-2xl font-bold text-slate-900">See how {domain} could look</p>
              <p className="text-slate-600 text-sm mt-1">Our AI Website Designer creates a modern homepage concept — no password or website access required.</p>
            </div>
            <button onClick={() => run(domain)} disabled={loading} data-testid="rebuild-preview-btn" className="bg-slate-900 text-white font-semibold rounded-xl px-6 py-3.5 flex items-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <>Create My Free Preview <ArrowUpRight size={16} /></>}
            </button>
          </div>
        )
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl card-shadow overflow-hidden" data-testid="rebuild-concept">
          <div className="bg-gradient-to-b from-emerald-50 to-white border-b border-emerald-100 p-6 md:p-8">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1"><Wand2 size={16} className="text-emerald-700" /><span className="text-xs font-bold uppercase tracking-wide text-emerald-700">Proposed Homepage · {dom}</span></div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 max-w-2xl">{c.hero?.headline}</h3>
                <p className="text-slate-600 mt-2 max-w-2xl">{c.hero?.subheadline}</p>
                {c.hero?.primary_cta && <span className="inline-block mt-4 bg-brand text-white text-sm font-semibold rounded-lg px-5 py-2.5">{c.hero.primary_cta}</span>}
              </div>
              <div className="flex flex-col items-end gap-3">
                {data.overall_score != null && (
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">Opportunity Score</p>
                    <p className="font-heading text-4xl font-extrabold" style={{ color: scoreTone(data.overall_score).color }}>{data.overall_score}<span className="text-lg text-slate-400">/100</span></p>
                  </div>
                )}
                <button onClick={primaryAction} disabled={downloading} data-testid="rebuild-pdf-link" className="bg-slate-900 text-white font-semibold rounded-xl px-5 py-3 flex items-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60">
                  {downloading ? <Loader2 className="animate-spin" size={16} /> : (unlocked ? <Download size={16} /> : <Lock size={16} />)}
                  {unlocked ? "Download full report" : "Get my full report"}
                </button>
              </div>
            </div>
            {!user && token && (
              <div className="mt-5 flex flex-wrap items-center gap-3 bg-emerald-100/60 border border-emerald-200 rounded-xl px-4 py-3" data-testid="rebuild-account-cta">
                <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
                <p className="text-slate-700 text-sm flex-1">Your report is unlocked. Create a free account to keep every redesign in your portal.</p>
                <Link to="/login" data-testid="rebuild-create-account-link" className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-semibold rounded-lg px-4 py-2 hover:-translate-y-0.5 transition-transform">
                  <UserPlus size={15} /> Create free account
                </Link>
              </div>
            )}
          </div>

          {data.content_readable === false && (
            <div className="mx-6 md:mx-8 mt-6 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="makeover-unreadable-notice">
              <AlertTriangle size={17} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">
                Heads up: we couldn't fully read <span className="font-semibold">{dom}</span>
                {data.block_reason === "bot-protection" || data.block_reason === "blocked" ? " (it blocks automated visits)" : data.block_reason === "thin-or-js-rendered" ? " (it's mostly JavaScript-rendered)" : ""}.
                {" "}This concept is based on limited public info and the business name — for a precise redesign, share a bit about your business or a page we can read.
              </p>
            </div>
          )}

          <div className="p-6 md:p-8 border-b border-slate-100">
            <span className="overline">3 Key Recommendations</span>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
              {recs.map((r, i) => (
                <div key={i} className="flex items-start gap-2 bg-slate-50 rounded-xl p-3">
                  <CheckCircle2 size={16} className="text-brand mt-0.5 shrink-0" />
                  <p className="text-sm text-slate-700 leading-snug">{r}</p>
                </div>
              ))}
            </div>
            {firstBA && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                <div className="border border-slate-200 rounded-xl p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Now · {firstBA.area}</p>
                  <p className="text-sm text-slate-600 mt-1">{firstBA.before}</p>
                </div>
                <div className="border border-brand/20 bg-brand/5 rounded-xl p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-brand">Proposed · {firstBA.area}</p>
                  <p className="text-sm text-slate-700 mt-1">{firstBA.after}</p>
                </div>
              </div>
            )}
          </div>

          <div className="p-6 md:p-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-3"><Palette size={15} className="text-brand" /><span className="overline">Style & Palette</span></div>
              <p className="text-slate-600 text-sm leading-relaxed">{c.style_direction}</p>
              <div className="flex gap-2 mt-4 flex-wrap">
                {(c.color_palette || []).map((p, i) => (
                  <div key={i} className="text-center">
                    <div className="w-12 h-12 rounded-lg border border-slate-200" style={{ background: p.hex }} />
                    <p className="text-[9px] font-mono text-slate-400 mt-1">{p.hex}</p>
                  </div>
                ))}
              </div>
              {c.navigation?.length > 0 && (
                <div className="mt-6">
                  <span className="overline">Recommended Navigation</span>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {c.navigation.map((n, i) => <span key={i} className="text-xs font-medium bg-slate-100 rounded px-2.5 py-1 text-slate-600">{n}</span>)}
                  </div>
                </div>
              )}
            </div>

            <div className="relative">
              <span className="overline">Full Report · Proposed Sections</span>
              <div className={`mt-3 space-y-2 ${unlocked ? "" : "blur-sm select-none pointer-events-none"}`} data-testid="rebuild-locked-report">
                {(c.sections || []).map((s, i) => (
                  <div key={i} className="border border-slate-100 rounded-lg p-3">
                    <p className="text-sm font-semibold text-slate-800">{s.title}</p>
                    <p className="text-xs text-slate-500">{s.purpose}</p>
                  </div>
                ))}
              </div>
              {!unlocked && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200 shadow-lg p-6 max-w-xs">
                    <Lock className="mx-auto text-brand mb-2" size={22} />
                    <p className="font-heading font-bold text-slate-900">Your complete redesign is ready</p>
                    <p className="text-xs text-slate-500 mt-1">Share your email or WhatsApp and we'll send the full PDF report — all proposed sections, SEO & AI opportunities.</p>
                    <button onClick={() => setGateOpen(true)} data-testid="rebuild-unlock-report-btn" className="w-full btn-primary rounded-xl px-4 py-2.5 mt-4 text-sm">Send me the full report</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {standalone && (
            <div className="px-6 md:px-8 pb-6">
              <button onClick={() => { setData(null); setToken(null); }} data-testid="makeover-reset" className="text-sm text-slate-500 hover:text-brand font-medium">← Try another website</button>
            </div>
          )}
        </div>
      )}

      {data && (
        <RebuildGateDialog
          open={gateOpen}
          onOpenChange={setGateOpen}
          conceptId={data.id}
          domain={dom}
          onUnlocked={({ token: t }) => { setToken(t); openPdf(t); }}
        />
      )}
    </div>
  );
}
